import { useEffect, useState } from "react";
import "@/App.css";
import "@/accessibility.css";
import "@/lib/bookingDraftPrivacy";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import PaymentExperience from "@/components/PaymentExperience";
import PaymentFocusBoundary from "@/components/PaymentFocusBoundary";
import Landing from "@/pages/Landing";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminBookingDetail from "@/pages/admin/AdminBookingDetail";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import AdminDevices from "@/pages/admin/AdminDevices";
import { Toaster } from "@/components/ui/sonner";

const DEFAULT_ROBOTS_CONTENT = "index,follow,max-image-preview:large";
const NOINDEX_ROBOTS_CONTENT = "noindex,nofollow";

function AppRouter({ lang, setLang }) {
  const location = useLocation();

  useEffect(() => {
    const robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) return undefined;

    const params = new URLSearchParams(location.search);
    const paymentState =
      params.has("payment") || params.has("session_id") || params.has("booking_id");
    const privateRoute = location.pathname.startsWith("/admin");
    const authCallback = location.hash?.includes("session_id=");

    robotsMeta.setAttribute(
      "content",
      privateRoute || paymentState || authCallback
        ? NOINDEX_ROBOTS_CONTENT
        : DEFAULT_ROBOTS_CONTENT,
    );

    return () => {
      robotsMeta.setAttribute("content", DEFAULT_ROBOTS_CONTENT);
    };
  }, [location.hash, location.pathname, location.search]);

  // CRITICAL: catch the OAuth fragment FIRST (synchronous, before normal routes)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing lang={lang} setLang={setLang} />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminBookings />} />
        <Route path="bookings/:id" element={<AdminBookingDetail />} />
        <Route path="calendar" element={<AdminCalendar />} />
        <Route path="devices" element={<AdminDevices />} />
      </Route>
    </Routes>
  );
}

function App() {
  const [lang, setLang] = useState("hu");
  return (
    <div className="App tk-bg-paper min-h-screen">
      <BrowserRouter>
        <PaymentExperience />
        <PaymentFocusBoundary />
        <AuthProvider>
          <AppRouter lang={lang} setLang={setLang} />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
